import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CBadge,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CSpinner,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilBook, cilCreditCard, cilLockLocked } from "@coreui/icons";
import {
  canResumeAddOn,
  type HeldAddOnDto,
  TRADINGVIEW_SCRIPTS_ADD_ON_TYPE,
  useAddOns,
} from "../../api/hooks/useAddOns";
import { isActiveSubscription, useMySubscriptions } from "../../api/hooks/useStrategySubscriptions";
import { type TradingViewAccountDto, useTradingViewAccount } from "../../api/hooks/useTradingViewAccount";
import { useTradingViewScripts } from "../../api/hooks/useTradingViewScripts";
import { formatDateShort } from "../../lib/dates";
import { formatPriceUsd } from "../../lib/money";
import {
  addOnDetailsPath,
  addOnEndPath,
  addOnResumePath,
  helpArticlePath,
  routePaths,
} from "../../routes/route-paths";
import { SubscriptionCard } from "../trading-strategies/MySubscriptions";
import { AddOnBuyPanel } from "./AddOnBuyPanel";

// One row on a wide screen: enough to show what it covers without repeating My strategies.
const PREVIEW_COUNT = 3;

/**
 * One add-on: whether the customer holds it, and if not, how to buy it. Each card on Add-ons opens it, and its top
 * card holds the one thing to do next, as a membership's does.
 */
export function AddOnDetails() {
  const { addOnId = "" } = useParams<{ addOnId: string }>();
  const { t } = useTranslation("addOns");
  const addOns = useAddOns();
  const subscriptions = useMySubscriptions();
  const tradingViewAccount = useTradingViewAccount();
  const tradingViewScripts = useTradingViewScripts();

  const isLoading =
    addOns.isLoading || subscriptions.isLoading || tradingViewAccount.isLoading || tradingViewScripts.isLoading;
  const loadError = addOns.error ?? subscriptions.error ?? tradingViewAccount.error ?? tradingViewScripts.error;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (loadError || !tradingViewAccount.data) {
    return <CAlert color="danger">{t("detail.loadError")}</CAlert>;
  }

  // Only live add-ons and the customer's own are listed, so anything else is not theirs to see.
  const addOn = addOns.data?.find((candidate) => candidate.id === addOnId);

  if (!addOn) {
    return (
      <CAlert color="info">
        {t("detail.notFound")} <Link to={routePaths.addOns}>{t("detail.browse")}</Link>
      </CAlert>
    );
  }

  // What the customer pays for it and until when, or that staff look after it. Null when that isn't known.
  const billingOf = (held: HeldAddOnDto): string | null => {
    if (!held.boughtHere) {
      return held.activeTo
        ? t("detail.staffManagedUntil", { date: formatDateShort(held.activeTo) })
        : t("detail.staffManaged");
    }

    if (held.activeTo) {
      return t("detail.paidTo", { date: formatDateShort(held.activeTo) });
    }

    if (held.priceUsd === null || held.renewsOn === null) {
      return null;
    }

    const renews = { price: formatPriceUsd(held.priceUsd), date: formatDateShort(held.renewsOn) };
    return held.billingCycle === "Yearly" ? t("detail.renewsYearly", renews) : t("detail.renewsMonthly", renews);
  };

  // In My strategies' order, so the first few here are the first few there.
  const activeSubscriptions = (subscriptions.data ?? [])
    .filter((subscription) => isActiveSubscription(subscription))
    .sort((a, b) => +new Date(b.activeFrom) - +new Date(a.activeFrom));

  const invitedAsByStrategyId = new Map(
    (tradingViewScripts.data ?? []).map((script) => [script.tradingStrategyId, script.tradingViewUsername]),
  );

  const held = addOn.held;
  const invitesTradingView = addOn.type === TRADINGVIEW_SCRIPTS_ADD_ON_TYPE;
  let pill: ReactNode = null;
  let action: ReactNode;
  const facts: { label: string; value: ReactNode }[] = [];

  if (held) {
    pill = held.activeTo ? (
      <CBadge color="warning" shape="rounded-pill">
        {t("status.endsOn", { date: formatDateShort(held.activeTo) })}
      </CBadge>
    ) : (
      <CBadge color="success" shape="rounded-pill">
        {t("status.active")}
      </CBadge>
    );
    action = held.boughtHere ? (
      <>
        <Link to={routePaths.billing} className="btn btn-primary">
          <CIcon icon={cilCreditCard} className="me-2" />
          {t("detail.billing")}
        </Link>
        {held.activeTo === null && (
          <Link to={addOnEndPath(addOn.id)} className="btn btn-outline-danger">
            {t("detail.end")}
          </Link>
        )}
        {canResumeAddOn(held) && (
          <Link to={addOnResumePath(addOn.id)} className="btn btn-success">
            {t("detail.resume")}
          </Link>
        )}
      </>
    ) : null;

    const billing = billingOf(held);
    facts.push({ label: t("detail.since"), value: formatDateShort(held.activeFrom) });
    if (billing) {
      facts.push({ label: t("detail.billing"), value: billing });
    }
    if (invitesTradingView) {
      facts.push({
        label: t("detail.tradingViewAccount"),
        value: <TradingViewAccountFact account={tradingViewAccount.data} returnTo={addOnDetailsPath(addOn.id)} />,
      });
    }
  } else {
    action = (
      <AddOnBuyPanel addOn={addOn} tradingViewAccount={invitesTradingView ? tradingViewAccount.data : null} />
    );
  }

  if (!held && invitesTradingView) {
    facts.push(
      {
        label: t("tradingViewScripts.covers"),
        value:
          activeSubscriptions.length > 0
            ? t("tradingViewScripts.coversToday", { count: activeSubscriptions.length })
            : t("tradingViewScripts.coversEvery"),
      },
      { label: t("tradingViewScripts.starts"), value: t("tradingViewScripts.startsWhenPaid") },
    );
  }

  return (
    <div>
      <CCard className="mb-4">
        <CCardBody className="d-flex justify-content-between align-items-start flex-wrap flex-lg-nowrap gap-4">
          <div style={{ minWidth: 0, flex: "1 1 320px" }}>
            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
              <h1 className="mb-0">{addOn.name}</h1>
              {pill}
            </div>
            <p className="text-body-secondary mb-0">{addOn.description}</p>
            {addOn.helpArticleSlug && (
              <div className="mt-3">
                <Link to={helpArticlePath(addOn.helpArticleSlug)} className="btn btn-outline-secondary">
                  <CIcon icon={cilBook} className="me-2" />
                  {t("detail.howItWorks")}
                </Link>
              </div>
            )}
            <div className="d-flex flex-wrap gap-4 mt-4 pt-3 border-top">
              {facts.map((fact) => (
                <div key={fact.label} style={{ minWidth: 0 }}>
                  <div className="small text-uppercase fw-semibold text-body-secondary">{fact.label}</div>
                  <div className="text-break">{fact.value}</div>
                </div>
              ))}
            </div>
          </div>
          {action && <div className="col-12 add-on-action-column d-grid gap-2">{action}</div>}
        </CCardBody>
      </CCard>

      {invitesTradingView && held && (
        <>
          <h2 className="h4 mb-3">{t("tradingViewScripts.scriptsHeading")}</h2>
          {activeSubscriptions.length > 0 && (
            <CRow xs={{ gutter: 4 }}>
              {activeSubscriptions.map((subscription) => (
                <CCol key={subscription.subscriptionExternalId} md={6} xl={4}>
                  <SubscriptionCard
                    subscriptions={[subscription]}
                    alsoIn={[]}
                    tradingViewInvitedAs={invitedAsByStrategyId.get(subscription.strategy.id) ?? null}
                  />
                </CCol>
              ))}
            </CRow>
          )}
          <p className="text-body-secondary mt-3 mb-0">{t("tradingViewScripts.scriptsLater")}</p>
        </>
      )}
      {invitesTradingView && !held && (
        <>
          <h2 className="h4 mb-3">{t("tradingViewScripts.coversHeading")}</h2>
          {activeSubscriptions.length > 0 ? (
            <>
              <CRow xs={{ gutter: 4 }}>
                {activeSubscriptions.slice(0, PREVIEW_COUNT).map((subscription) => (
                  <CCol key={subscription.subscriptionExternalId} md={6} xl={4}>
                    <SubscriptionCard subscriptions={[subscription]} alsoIn={[]} />
                  </CCol>
                ))}
              </CRow>
              <p className="text-body-secondary mt-3 mb-0">{t("tradingViewScripts.coversLater")}</p>
            </>
          ) : (
            <div className="d-flex align-items-center flex-wrap gap-3">
              <p className="text-body-secondary mb-0">{t("tradingViewScripts.coversNothingYet")}</p>
              <Link to={routePaths.strategies} className="btn btn-outline-primary btn-sm">
                {t("detail.browseStrategies")}
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The account the add-on invites. Staff give the add-on without one, so a holder with none is sent to add it; once
 * it's set it is locked to the add-on.
 */
function TradingViewAccountFact({ account, returnTo }: { account: TradingViewAccountDto; returnTo: string }) {
  const { t } = useTranslation("addOns");

  if (account.username) {
    return (
      <span className="d-inline-flex align-items-center gap-2">
        {account.locked && <CIcon icon={cilLockLocked} className="text-body-secondary" aria-hidden />}
        {account.username}
      </span>
    );
  }

  return (
    <span className="d-inline-flex align-items-center flex-wrap gap-2">
      <span className="text-body-secondary fst-italic">{t("detail.notAddedYet")}</span>
      <Link to={routePaths.profileTradingView} state={{ returnTo }} className="btn btn-outline-primary btn-sm">
        {t("detail.addUsername")}
      </Link>
    </span>
  );
}

export default AddOnDetails;
